/**
 * AI Frame Recommendations
 * Analyzes artwork colors using Canvas 2D, then suggests matching frames and mats.
 * Uses k-means color quantization (client-side, no external API needed).
 */

import { useState, useCallback, useMemo } from 'react';
import { Button, cn } from '@frameit/ui';
import { useProjectStore, useSelectedArtwork, getLocalImageUrl } from '../../stores/projectStore';
import { FRAMES, type FrameData } from '../../data/frames';
import { MATS, type MatData } from '../../data/mats';

/** RGB color */
interface RGB {
  r: number;
  g: number;
  b: number;
}

/** Hex to RGB */
function hexToRgb(hex: string): RGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : { r: 128, g: 128, b: 128 };
}

/** Euclidean distance between two colors */
function colorDistance(a: RGB, b: RGB): number {
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
}

/** Get perceived brightness (0-255) */
function brightness(c: RGB): number {
  return (c.r * 299 + c.g * 587 + c.b * 114) / 1000;
}

/** Get color warmth (-1 cold to +1 warm) */
function warmth(c: RGB): number {
  const warm = (c.r * 2 + c.g) / 3;
  const cool = (c.b * 2 + c.g) / 3;
  return (warm - cool) / 255;
}

/** Get color saturation (0-1) */
function saturation(c: RGB): number {
  const max = Math.max(c.r, c.g, c.b);
  const min = Math.min(c.r, c.g, c.b);
  return max === 0 ? 0 : (max - min) / max;
}

/** K-means color quantization — extract dominant colors from image data */
function extractDominantColors(
  imageData: ImageData,
  k: number = 5,
  iterations: number = 10,
): RGB[] {
  const pixels: RGB[] = [];

  // Sample pixels (every 4th pixel to speed up)
  for (let i = 0; i < imageData.data.length; i += 16) {
    pixels.push({
      r: imageData.data[i],
      g: imageData.data[i + 1],
      b: imageData.data[i + 2],
    });
  }

  if (pixels.length === 0) return [];

  // Initialize centroids using k-means++ seeding
  const centroids: RGB[] = [pixels[Math.floor(Math.random() * pixels.length)]];

  for (let c = 1; c < k; c++) {
    const distances = pixels.map((p) => {
      const minDist = Math.min(...centroids.map((centroid) => colorDistance(p, centroid)));
      return minDist * minDist;
    });
    const totalDist = distances.reduce((a, b) => a + b, 0);
    let target = Math.random() * totalDist;
    for (let i = 0; i < pixels.length; i++) {
      target -= distances[i];
      if (target <= 0) {
        centroids.push(pixels[i]);
        break;
      }
    }
  }

  // K-means iterations
  for (let iter = 0; iter < iterations; iter++) {
    const clusters: RGB[][] = Array.from({ length: k }, () => []);

    // Assign pixels to nearest centroid
    for (const pixel of pixels) {
      let minDist = Infinity;
      let minIdx = 0;
      for (let c = 0; c < centroids.length; c++) {
        const dist = colorDistance(pixel, centroids[c]);
        if (dist < minDist) {
          minDist = dist;
          minIdx = c;
        }
      }
      clusters[minIdx].push(pixel);
    }

    // Update centroids
    for (let c = 0; c < k; c++) {
      if (clusters[c].length === 0) continue;
      centroids[c] = {
        r: Math.round(clusters[c].reduce((s, p) => s + p.r, 0) / clusters[c].length),
        g: Math.round(clusters[c].reduce((s, p) => s + p.g, 0) / clusters[c].length),
        b: Math.round(clusters[c].reduce((s, p) => s + p.b, 0) / clusters[c].length),
      };
    }
  }

  // Sort by prominence (cluster size)
  return centroids;
}

/** Score how well a frame matches the artwork palette */
function scoreFrame(frame: FrameData, palette: RGB[]): number {
  const frameRgb = hexToRgb(frame.color);
  const avgBright = palette.reduce((s, c) => s + brightness(c), 0) / palette.length;
  const avgWarmth = palette.reduce((s, c) => s + warmth(c), 0) / palette.length;
  const avgSat = palette.reduce((s, c) => s + saturation(c), 0) / palette.length;
  const frameBright = brightness(frameRgb);
  const frameWarmth = warmth(frameRgb);

  let score = 0;

  // Contrast: frame should contrast with artwork (not match exactly)
  const brightContrast = Math.abs(frameBright - avgBright);
  score += brightContrast > 60 ? 20 : brightContrast > 30 ? 10 : 0;

  // Temperature harmony: warm artwork → warm frame, cool → cool
  const tempMatch = Math.abs(frameWarmth - avgWarmth);
  score += tempMatch < 0.3 ? 15 : tempMatch < 0.5 ? 8 : 0;

  // Complementary color bonus: find closest palette color
  const minDist = Math.min(...palette.map((c) => colorDistance(c, frameRgb)));
  // Not too close (would blend), not too far (would clash)
  if (minDist > 50 && minDist < 200) score += 15;
  if (minDist > 80 && minDist < 150) score += 10;

  // Saturation harmony: low-sat artwork → neutral frame, high-sat → can handle bold
  if (avgSat < 0.3 && saturation(frameRgb) < 0.3) score += 10;
  if (avgSat > 0.5 && saturation(frameRgb) > 0.2) score += 5;

  // Classic choice bonus: black/white/natural wood always work
  if (frameBright < 40 || frameBright > 230) score += 8;

  return score;
}

/** Score how well a mat matches the artwork palette */
function scoreMat(mat: MatData, palette: RGB[]): number {
  const matRgb = hexToRgb(mat.color);
  const avgBright = palette.reduce((s, c) => s + brightness(c), 0) / palette.length;
  const matBright = brightness(matRgb);

  let score = 0;

  // Mats should be lighter than artwork for contrast
  if (matBright > avgBright) score += 15;

  // Neutral mats score higher (white/cream family)
  if (mat.category === 'white' || mat.category === 'neutral') score += 12;

  // Find a palette color the mat complements (subtle tint match)
  const minDist = Math.min(...palette.map((c) => colorDistance(c, matRgb)));
  if (minDist > 100 && minDist < 250) score += 10;

  // Warm artwork → warm mat tint
  const avgWarm = palette.reduce((s, c) => s + warmth(c), 0) / palette.length;
  const matWarm = warmth(matRgb);
  if (Math.abs(avgWarm - matWarm) < 0.3) score += 8;

  return score;
}

/** Recommendation result */
interface Recommendation {
  frameId: string;
  frameName: string;
  frameColor: string;
  matId: string | undefined;
  matName: string | undefined;
  matColor: string | undefined;
  score: number;
}

export interface AIRecommendationsProps {
  className?: string;
}

/** AI frame/mat recommendation component */
export function AIRecommendations({ className }: AIRecommendationsProps): React.JSX.Element | null {
  const selectedArtwork = useSelectedArtwork();
  const updateFrame = useProjectStore((s) => s.updateFrame);
  const updateMat = useProjectStore((s) => s.updateMat);
  const saveToHistory = useProjectStore((s) => s.saveToHistory);

  const [recommendations, setRecommendations] = useState<Recommendation[] | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [palette, setPalette] = useState<RGB[] | null>(null);

  const imageUrl = useMemo(() => {
    if (!selectedArtwork) return null;
    return getLocalImageUrl(selectedArtwork.artworkId) ?? null;
  }, [selectedArtwork]);

  const analyze = useCallback((): void => {
    if (!imageUrl || !selectedArtwork) return;

    setIsAnalyzing(true);

    // Load image onto canvas for pixel analysis
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Downscale to 64px for fast analysis
      const size = 64;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setIsAnalyzing(false);
        return;
      }

      ctx.drawImage(img, 0, 0, size, size);
      const imageData = ctx.getImageData(0, 0, size, size);

      // Extract dominant colors
      const colors = extractDominantColors(imageData, 5, 15);
      setPalette(colors);

      // Score all frames
      const frameScores = FRAMES.filter((f) => !f.isPremium) // Only suggest non-premium by default
        .map((f) => ({ frame: f, score: scoreFrame(f, colors) }))
        .sort((a, b) => b.score - a.score);

      // Score all mats
      const matScores = MATS.filter((m) => !m.isPremium)
        .map((m) => ({ mat: m, score: scoreMat(m, colors) }))
        .sort((a, b) => b.score - a.score);

      // Build top 4 recommendations: best frames paired with best mats
      const recs: Recommendation[] = [];
      const topFrames = frameScores.slice(0, 4);
      const topMats = matScores.slice(0, 3);

      for (let i = 0; i < topFrames.length; i++) {
        const frame = topFrames[i];
        const mat = i < topMats.length ? topMats[i] : topMats[0];
        recs.push({
          frameId: frame.frame.id,
          frameName: frame.frame.name,
          frameColor: frame.frame.color,
          matId: mat?.mat.id,
          matName: mat?.mat.name,
          matColor: mat?.mat.color,
          score: frame.score + (mat?.score ?? 0),
        });
      }

      setRecommendations(recs);
      setIsAnalyzing(false);
    };

    img.onerror = () => {
      setIsAnalyzing(false);
    };

    img.src = imageUrl;
  }, [imageUrl, selectedArtwork]);

  const handleApply = (rec: Recommendation): void => {
    if (!selectedArtwork) return;
    saveToHistory();
    const frameData = FRAMES.find((f) => f.id === rec.frameId);
    updateFrame(selectedArtwork.id, {
      templateId: rec.frameId,
      width: frameData?.defaultWidth ?? 25,
    });
    if (rec.matId) {
      updateMat(selectedArtwork.id, { templateId: rec.matId, width: 0.1 });
    }
  };

  if (!selectedArtwork || !imageUrl) return null;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Analyze button */}
      {!recommendations && (
        <Button size="sm" className="w-full" onClick={analyze} disabled={isAnalyzing}>
          {isAnalyzing ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-1.5" />
              Analyzing colors...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              AI Suggest
            </>
          )}
        </Button>
      )}

      {/* Color palette */}
      {palette && (
        <div>
          <label className="text-[10px] text-muted-foreground block mb-1">Detected Palette</label>
          <div className="flex gap-1">
            {palette.map((c, i) => (
              <div
                key={i}
                className="w-6 h-6 rounded-sm border"
                style={{ backgroundColor: `rgb(${c.r},${c.g},${c.b})` }}
                title={`RGB(${c.r}, ${c.g}, ${c.b})`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium">Recommended</label>
            <button
              onClick={() => {
                setRecommendations(null);
                setPalette(null);
              }}
              className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Re-analyze
            </button>
          </div>

          {recommendations.map((rec) => (
            <button
              key={rec.frameId}
              onClick={() => handleApply(rec)}
              className="w-full flex items-center gap-2 p-2 rounded-lg border hover:bg-accent transition-colors text-left"
            >
              {/* Frame + mat color preview */}
              <div className="relative w-10 h-10 rounded border flex-shrink-0">
                <div
                  className="absolute inset-0 rounded"
                  style={{ backgroundColor: rec.frameColor }}
                />
                {rec.matColor && (
                  <div
                    className="absolute inset-1.5 rounded-sm"
                    style={{ backgroundColor: rec.matColor }}
                  />
                )}
              </div>

              {/* Details */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{rec.frameName}</p>
                {rec.matName && (
                  <p className="text-[10px] text-muted-foreground truncate">+ {rec.matName} mat</p>
                )}
              </div>

              {/* Score badge */}
              <div className="flex-shrink-0 px-1.5 py-0.5 bg-primary/10 text-primary text-[10px] rounded">
                {Math.round(rec.score)}%
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
